import React, { useState, useCallback, type ChangeEvent, useEffect } from 'react';
import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import { Field, ErrorMessage } from '@atlaskit/form';
import { Box, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useDebouncedCallback } from '@atlassian/jira-platform-use-debounce';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { DEBOUNCE_DELAY } from '../../common/constants';
import { CSV, type ImportType, type SupportedThirdParty } from '../../common/constants/import-type';
import { X2C_SCREENS } from '../../common/constants/metrics';
import {
	PROJECT_NAME_END_LIMIT,
	PROJECT_NAME_START_LIMIT,
	ProjectStyles,
	ProjectTypes,
} from '../../common/constants/project';
import type { Template } from '../../common/constants/steps';
import { PageDescription, PageHeading } from '../../common/ui';
import { StepFooterStandard } from '../../common/ui/step-footer';
import { ProgressBarStandard } from '../../common/ui/step-progress-bar';
import { StepScreen } from '../../common/ui/step-screen';
import { useContentForCSV } from '../../common/utils/get-content-for-csv';
import { useProjectData, useThirdPartyState } from '../../controllers/import-state';
import { useSelectedSourceItem } from '../../controllers/import-state/selectors';
import { useStartImportController } from '../../controllers/start-import';
import { useProductSteps } from '../../controllers/utils';
import { useGenerateProjectKey } from '../../services/generate-project-key';
import { useValidateProjectKey } from '../../services/validate-project-key';
import { useValidateProjectName } from '../../services/validate-project-name';
import { IllustrationNew } from './illustration/main';
import messages from './messages';
import { NewProjectCreateMoreOptions } from './new-project-create-more-options';
import { ProjectCreateStats } from './project-create-stats';
import { ProjectKeyField } from './project-key-field';
import { ProjectTypeSelector } from './project-type-selector';
import { TemplateSelector } from './template-selector';

export type ProjectCreateScreenNewProps = {
	onReturn: () => void;
	onNext: () => void;
	onCloseAndReturnToUploadStep: () => void;
	shouldPrefillThirdPartyProjectName?: boolean;
	shouldShowThirdPartyStats?: boolean;
	thirdParty?: SupportedThirdParty;
	shouldStartImportOnNext: boolean;
	templates: Template[];
	importType?: ImportType;
};

export const ProjectCreateScreenNew = ({
	onReturn,
	onNext,
	shouldPrefillThirdPartyProjectName = false,
	shouldShowThirdPartyStats = false,
	thirdParty,
	shouldStartImportOnNext,
	onCloseAndReturnToUploadStep,
	templates,
	importType = CSV,
}: ProjectCreateScreenNewProps) => {
	const { formatMessage } = useIntl();
	const { isLoading: isLoadingProjectNameValidation, validateProjectName } =
		useValidateProjectName();
	const { isLoading: isLoadingProjectKeyValidation, validateProjectKey } = useValidateProjectKey();
	const { isLoading: isLoadingProjectKey, generateProjectKey } = useGenerateProjectKey();
	const [projectData, { setProjectData }] = useProjectData();
	const [{ selectedSpaceName, selectedEntityName, selectedEntityStyle, selectedEntity }] =
		useThirdPartyState();
	const [selectedSourceItem] = useSelectedSourceItem();
	const projectSettings = selectedSourceItem?.[selectedSourceItem?.supportedFlow];
	const { setupProjectScreenDescription } = useContentForCSV(thirdParty || importType);

	let DEFAULT_PROJECT_NAME = '';
	if (fg('jira_importer_third_party_state_refactor_2')) {
		if (shouldPrefillThirdPartyProjectName && selectedEntity?.name) {
			if (selectedSpaceName) {
				DEFAULT_PROJECT_NAME = `${selectedSpaceName} - ${selectedEntity?.name}`;
			} else {
				DEFAULT_PROJECT_NAME = selectedEntity?.name;
			}
		}
	} else if (shouldPrefillThirdPartyProjectName && selectedEntityName) {
		if (selectedSpaceName) {
			DEFAULT_PROJECT_NAME = `${selectedSpaceName} - ${selectedEntityName}`;
		} else {
			DEFAULT_PROJECT_NAME = selectedEntityName;
		}
	}
	const [projectName, setProjectName] = useState<string>(projectData.name || DEFAULT_PROJECT_NAME);
	const [projectKey, setProjectKey] = useState<string>(projectData.key || '');
	const [projectNameValidationError, setProjectNameValidationError] = useState<string>('');
	const [projectKeyValidationError, setProjectKeyValidationError] = useState<string>('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { productSteps, currentStepIndex } = useProductSteps();
	const { startImport, isImportLoading, hasImportErrorOccurred, isStartImportDisabled } =
		useStartImportController({ thirdParty });

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const shouldDisableNext =
		projectName === '' ||
		projectKey === '' ||
		(!isSubmitting &&
			(projectNameValidationError !== '' ||
				projectKeyValidationError !== '' ||
				isLoadingProjectNameValidation ||
				isLoadingProjectKeyValidation ||
				isLoadingProjectKey));

	const shouldDisableImport = isStartImportDisabled || shouldDisableNext;

	useEffect(() => {
		if (!shouldPrefillThirdPartyProjectName || !projectName) {
			return;
		}

		validateProjectName(projectName).then((errorMessage) => {
			if (errorMessage) {
				setProjectNameValidationError(errorMessage);
				return;
			}
			setProjectNameValidationError('');
			generateProjectKey(projectName).then((generatedProjectKey) => {
				if (!isNull(generatedProjectKey)) {
					setProjectKey(generatedProjectKey);
					setProjectKeyValidationError('');
					setProjectData({ name: projectName, key: generatedProjectKey });
				}
			});
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [generateProjectKey]);

	const [handleProjectNameChange] = useDebouncedCallback((newProjectName: string) => {
		const valueLength = newProjectName.trim().length;
		if (valueLength === 0) {
			setProjectNameValidationError(formatMessage(messages.projectNameEmpty));
			return;
		}
		if (valueLength < PROJECT_NAME_START_LIMIT) {
			setProjectNameValidationError(formatMessage(messages.projectNameTooShort));
			return;
		}
		if (valueLength > PROJECT_NAME_END_LIMIT) {
			setProjectNameValidationError(formatMessage(messages.projectNameTooLong));
			return;
		}

		validateProjectName(newProjectName).then((errorMessage) => {
			if (errorMessage) {
				setProjectNameValidationError(errorMessage);
				return;
			}
			setProjectNameValidationError('');
			generateProjectKey(newProjectName).then((generatedProjectKey) => {
				if (!isNull(generatedProjectKey)) {
					setProjectKey(generatedProjectKey);
					setProjectKeyValidationError('');
					setProjectData({ name: newProjectName, key: generatedProjectKey });
				}
			});
		});
	}, DEBOUNCE_DELAY);

	const onProjectNameChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setProjectName(e.target.value);
			setProjectNameValidationError('');
			handleProjectNameChange(e.target.value);
		},
		[setProjectName, handleProjectNameChange, setProjectNameValidationError],
	);

	const [handleProjectKeyChange] = useDebouncedCallback((newProjectKey: string) => {
		validateProjectKey(newProjectKey).then((errorMessage) => {
			if (errorMessage) {
				setProjectKeyValidationError(errorMessage);
				return;
			}
			setProjectKeyValidationError('');
			setProjectData({ key: newProjectKey });
		});
	}, DEBOUNCE_DELAY);

	const onProjectKeyChange = useCallback(
		(value: string) => {
			setProjectKey(value);
			setProjectKeyValidationError('');
			handleProjectKeyChange(value);
		},
		[handleProjectKeyChange, setProjectKey, setProjectKeyValidationError],
	);

	const onSubmit = useCallback(async () => {
		// Not possible since users would have to have loaded mapping csv in the previous screen
		// and submit button is disabled in this case
		if (shouldStartImportOnNext && shouldDisableImport) {
			return;
		}

		setIsSubmitting(true);

		const projectNameError = await validateProjectName(projectName);
		if (projectNameError) {
			setIsSubmitting(false);
			setProjectNameValidationError(projectNameError);
			return;
		}

		const projectKeyError = await validateProjectKey(projectKey);

		if (projectKeyError) {
			setIsSubmitting(false);
			setProjectKeyValidationError(projectKeyError);
			return;
		}

		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'projectSetupNextButton',
			}),
			{
				template: projectData.template,
				accessLevel: projectData.accessLevel,
			},
		);

		if (!shouldStartImportOnNext) {
			onNext();
			return;
		}

		startImport({ projectData, onNext });
		setIsSubmitting(false);
	}, [
		shouldStartImportOnNext,
		shouldDisableImport,
		validateProjectName,
		projectName,
		validateProjectKey,
		projectKey,
		startImport,
		projectData,
		onNext,
		createAnalyticsEvent,
	]);

	return (
		<ContextualAnalyticsData
			sourceName={X2C_SCREENS.SETUP_PROJECT}
			sourceType={SCREEN}
			attributes={{ thirdParty }}
		>
			<StepScreen
				testId="project-create-importer-upload-flow.ui.project-create-screen.wrapper"
				renderProgressBar={
					<ProgressBarStandard
						productSteps={productSteps}
						currentStepIndex={currentStepIndex}
						onClose={onCloseAndReturnToUploadStep}
					/>
				}
				renderFooter={
					<StepFooterStandard
						alignRight
						onReturn={onReturn}
						shouldStartImportOnNext={shouldStartImportOnNext}
						isLoading={isImportLoading || isSubmitting}
						shouldDisableReturn={isImportLoading || isSubmitting}
						shouldDisableNext={shouldStartImportOnNext ? shouldDisableImport : shouldDisableNext}
						nextButtonType="submit"
						hasImportErrorOccurred={hasImportErrorOccurred}
						onClickNext={() => onSubmit()}
					/>
				}
			>
				<Box xcss={contentWrapperStyles}>
					<Box xcss={formWrapperStyles}>
						<PageHeading>{formatMessage(messages.header)}</PageHeading>
						<PageDescription>{setupProjectScreenDescription}</PageDescription>
						<PageDescription>{formatMessage(messages.allFieldsRequired)}</PageDescription>
						<Box xcss={createFormWrapperStyles}>
							<Box xcss={fieldsWrapperStyles}>
								{projectSettings && <ProjectTypeSelector projectSetting={projectSettings} />}
								{projectData.type === ProjectTypes.SOFTWARE && templates.length > 0 && (
									<TemplateSelector templates={templates} />
								)}
								<Field label={formatMessage(messages.projectName)} name="name">
									{({ fieldProps }) => (
										<>
											<TextField
												testId="project-create-importer-upload-flow.ui.project-create-screen.project-name-field"
												{...fieldProps}
												value={projectName}
												onChange={onProjectNameChange}
												placeholder={formatMessage(messages.projectNamePlaceholder)}
											/>
											{projectNameValidationError !== '' && (
												<ErrorMessage testId="project-create-importer-upload-flow.ui.project-create-screen.project-name-error-message">
													{projectNameValidationError}
												</ErrorMessage>
											)}
										</>
									)}
								</Field>
							</Box>
							{shouldShowThirdPartyStats && thirdParty && (
								<ProjectCreateStats thirdParty={thirdParty} />
							)}
							<NewProjectCreateMoreOptions
								projectSettings={projectSettings}
								selectedEntityStyle={
									fg('jira_importer_third_party_state_refactor_2')
										? selectedEntity?.style
										: selectedEntityStyle
								}
								disableProjectStyle={
									!isNil(
										fg('jira_importer_third_party_state_refactor_2')
											? selectedEntity?.style
											: selectedEntityStyle,
									)
								}
							>
								<ProjectKeyField
									projectKey={projectKey}
									onProjectKeyChange={onProjectKeyChange}
									projectKeyValidationError={projectKeyValidationError}
								/>
							</NewProjectCreateMoreOptions>
						</Box>
					</Box>
					<IllustrationNew
						projectType={projectData.type}
						projectName={projectName}
						projectKey={projectKey}
						isTmp={projectData.style === ProjectStyles.TEAM_MANAGED_PROJECT}
						projectTemplate={projectData.template}
					/>
				</Box>
				<FireScreenAnalytics />
			</StepScreen>
		</ContextualAnalyticsData>
	);
};

const contentWrapperStyles = xcss({
	display: 'flex',
	flexFlow: 'row',
	flexWrap: 'wrap',
	columnGap: 'space.400',
	justifyContent: 'space-between',
});

const formWrapperStyles = xcss({
	display: 'flex',
	flexFlow: 'column',
	width: '500px',
});

const createFormWrapperStyles = xcss({
	display: 'flex',
	flexFlow: 'column',
	width: '400px',
});

const fieldsWrapperStyles = xcss({
	display: 'flex',
	flexFlow: 'inherit',
	rowGap: 'space.100',
});
